import * as React from "react";

import { useTheme } from "@emotion/react";
import { ampli } from "@hl/shared-features/lib/ampli";
import CollectionActivity from "@hl/shared-features/lib/features/marketplace/activity/CollectionActivity";
import { useDisabledMarketplaceFF } from "@hl/shared-features/lib/features/marketplace/hooks/useMarketplaces";
import { Tabs } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import CollectorsListTab from "~features/MintPage/apollo/CollectorsListTab";
import MarketplaceOffersList from "~features/MintPage/apollo/MarketplaceOffersList";
import { shloms404OnImageError } from "~features/MintPage/custom/Shloms404";
import { MarketplaceTokensApollo } from "~features/marketplace/MarketplaceTokensApollo";
import MarketplacePageSkeletonApollo from "~features/marketplace/skeleton/MarketplacePageSkeletonApollo";
import useMintState from "~hooks/useMintState";
import useMintTokensState from "~hooks/useMintTokensState";

const MintMarketplace = ({
  onTokensComponentMount,
}: {
  onTokensComponentMount: () => void;
}) => {
  const {
    collection,
    isMarketplaceEnabledForCollectionChain,
    isERC1155,
    isImported,
    isSingleContractEdition,
    enableCollectionOffer,
  } = useMintState();
  const disabledMarketplace = useDisabledMarketplaceFF();
  const { browseCollection } = useMintTokensState();
  const collectionId = collection?.id;
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm - 1}px)`);

  if (!browseCollection || !collectionId) {
    return <MarketplacePageSkeletonApollo />;
  }

  return (
    <Tabs defaultValue="artwork" keepMounted={false} mt={40}>
      <Tabs.List mb={isMobile ? 0 : 24}>
        <Tabs.Tab value="artwork" py="md">
          Artwork
        </Tabs.Tab>
        {isMarketplaceEnabledForCollectionChain && (
          <Tabs.Tab
            value="activity"
            py="md"
            onClick={() => {
              if (!ampli.isLoaded) {
                return;
              }
              ampli.viewActivity({
                projectName: collection.name,
              });
            }}
          >
            Activity
          </Tabs.Tab>
        )}
        <Tabs.Tab value="collectors" py="md">
          Collectors
        </Tabs.Tab>
        {!disabledMarketplace && enableCollectionOffer && (
          <Tabs.Tab value="offers" py="md">
            Offers
          </Tabs.Tab>
        )}
      </Tabs.List>
      <Tabs.Panel value="artwork">
        <MarketplaceTokensApollo
          collectionId={collectionId}
          scrollBodyOnFilterChange
          onComponentMount={onTokensComponentMount}
        />
      </Tabs.Panel>
      <Tabs.Panel value="activity" pt="md">
        <CollectionActivity
          disableChart={!(isSingleContractEdition || isImported) || isERC1155}
          collection={collection}
          isImported={isImported}
          onImageError={
            collection.flagVariations.shloms404UI
              ? shloms404OnImageError
              : undefined
          }
        />
      </Tabs.Panel>
      <Tabs.Panel value="collectors" pt="md">
        <CollectorsListTab collectionId={collectionId} />
      </Tabs.Panel>
      {enableCollectionOffer && (
        <Tabs.Panel value="offers" pt={0}>
          <MarketplaceOffersList />
        </Tabs.Panel>
      )}
    </Tabs>
  );
};

export default MintMarketplace;
